import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, BookOpen } from "lucide-react";
import { useLang } from "../LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const CATEGORY_LABEL = {
  editorial: { en: "Editorial", fr: "Éditorial" },
  tips: { en: "Tips", fr: "Conseils" },
  aftercare: { en: "Aftercare", fr: "Soins post-traitement" },
  announcement: { en: "Announcement", fr: "Annonce" },
};

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ""; }
};

export default function JournalPost() {
  const { slug } = useParams();
  const { lang } = useLang();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    axios
      .get(`${API}/journal/${slug}`)
      .then((r) => {
        setPost(r.data.post);
        setRelated(r.data.related || []);
        // scroll to top for the new article
        window.scrollTo({ top: 0, behavior: "instant" });
      })
      .catch((e) => {
        if (e.response?.status === 404) setNotFound(true);
      });
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-32 min-h-screen px-6 md:px-12 lg:px-20 py-14 text-center">
        <BookOpen size={30} strokeWidth={0.7} className="text-primary mx-auto mb-6" />
        <h1 className="font-serif text-3xl text-foreground mb-4 font-light">
          {lang === "fr" ? "Article introuvable." : "Article not found."}
        </h1>
        <Link to="/journal" className="qw-link-underline text-primary text-[0.7rem] tracking-[0.25em] uppercase">
          {lang === "fr" ? "Retour au journal" : "Back to the journal"}
        </Link>
      </main>
    );
  }

  if (!post) return <main className="pt-32 min-h-screen px-6" />;

  const title = lang === "fr" ? post.title_fr : post.title_en;
  const body = lang === "fr" ? post.body_fr : post.body_en;
  const excerpt = lang === "fr" ? post.excerpt_fr : post.excerpt_en;

  return (
    <main data-testid="journal-post-page" className="pt-32 min-h-screen">
      <article>
        {/* Header */}
        <section className="px-6 md:px-12 lg:px-20 pb-10">
          <button
            onClick={() => nav("/journal")}
            data-testid="journal-back-link"
            className="qw-link-underline inline-flex items-center gap-2 text-[0.65rem] tracking-[0.25em] uppercase text-foreground/70 hover:text-primary mb-10"
          >
            <ArrowLeft size={11} strokeWidth={1.2} />
            {lang === "fr" ? "Journal" : "Journal"}
          </button>
          <div className="max-w-3xl">
            <div className="flex items-baseline gap-3 mb-6">
              <p className="qw-overline text-primary">
                {CATEGORY_LABEL[post.category]?.[lang] || post.category}
              </p>
              <span className="text-foreground/30">·</span>
              <p className="text-[0.65rem] tracking-wider uppercase text-muted-foreground">
                {fmtDate(post.published_at, lang)}
              </p>
            </div>
            <h1 data-testid="journal-post-title" className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground font-light leading-tight tracking-tight mb-8 text-balance">
              {title}
            </h1>
            {excerpt && (
              <p className="font-serif text-lg md:text-xl text-muted-foreground italic leading-relaxed">
                {excerpt}
              </p>
            )}
          </div>
        </section>

        {/* Cover image */}
        {post.cover_image && (
          <div className="px-6 md:px-12 lg:px-20 mb-14 md:mb-20">
            <div className="aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-muted">
              <img
                src={resolveImg(post.cover_image)}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Body */}
        <section className="px-6 md:px-12 lg:px-20 pb-20">
          <div data-testid="journal-post-body" className="max-w-2xl mx-auto space-y-6 text-base md:text-lg text-foreground/85 leading-[1.85] font-light">
            {(body || "").split(/\n\s*\n/).map((para, i) => (
              <p key={i} className="whitespace-pre-line">{para}</p>
            ))}
          </div>

          {/* CTA */}
          <div className="max-w-2xl mx-auto mt-16 border-t border-border pt-10">
            <p className="qw-overline mb-4">{lang === "fr" ? "Envie d'essayer ?" : "Ready to try it?"}</p>
            <h3 className="font-serif text-2xl md:text-3xl text-foreground font-light mb-6 leading-snug">
              {lang === "fr"
                ? "Réservez votre consultation privée."
                : "Book your private consultation."}
            </h3>
            <Link to="/booking" className="qw-btn qw-btn-primary">
              {lang === "fr" ? "Réserver une visite" : "Reserve a visit"}
              <ArrowUpRight size={14} strokeWidth={1.2} />
            </Link>
          </div>
        </section>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="px-6 md:px-12 lg:px-20 pb-20 border-t border-border pt-16">
          <p className="qw-overline mb-8">{lang === "fr" ? "À lire aussi" : "Also read"}</p>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {related.map((p) => (
              <li key={p.id} data-testid={`related-${p.slug}`}>
                <Link to={`/journal/${p.slug}`} className="group block">
                  <div className="aspect-[4/5] overflow-hidden bg-muted mb-5">
                    {p.cover_image ? (
                      <img src={resolveImg(p.cover_image)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                        <BookOpen size={22} strokeWidth={0.7} className="text-primary/60" />
                      </div>
                    )}
                  </div>
                  <p className="qw-overline text-primary mb-2">
                    {CATEGORY_LABEL[p.category]?.[lang] || p.category}
                  </p>
                  <h4 className="font-serif text-xl text-foreground group-hover:text-primary transition-colors leading-tight">
                    {lang === "fr" ? p.title_fr : p.title_en}
                  </h4>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
